import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Plus, Trash2, Users, UserPlus, Briefcase, Truck, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { PageHeader, Scroller, Spinner, EmptyState, goldBtn } from "@/pages/_shared";

const CFG = {
  customers: { title: "Customers", icon: Users, editPerm: "customers.edit", fields: ["name", "email", "phone", "notes"] },
  leads: { title: "Leads", icon: UserPlus, editPerm: "leads.edit", fields: ["name", "email", "phone", "source", "stage"] },
  employees: { title: "Employees", icon: Briefcase, editPerm: "employees.edit", fields: ["name", "email", "role", "phone"] },
  suppliers: { title: "Suppliers", icon: Truck, editPerm: "suppliers.edit", fields: ["name", "email", "phone", "category"] },
};

export default function EntityList() {
  const { entity } = useParams();
  const cfg = CFG[entity];
  const { can, activeCompanyId } = useOrg();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/biz/${entity}`); setRows(data); }
    catch (e) { setRows([]); }
  }, [entity]);
  useEffect(() => { setRows(null); setForm({}); load(); }, [entity, load, activeCompanyId]);

  if (!cfg) return <EmptyState title="Unknown section" />;
  if (rows === null) return <Spinner />;

  const create = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    await api.post(`/biz/${entity}`, { data: form });
    setOpen(false); setForm({}); toast.success(`${cfg.title.slice(0, -1)} added`); load();
  };
  const del = async (id) => { await api.delete(`/biz/${entity}/${id}`); load(); };

  const Icon = cfg.icon;
  return (
    <Scroller>
      <PageHeader title={cfg.title} subtitle={`Manage your ${cfg.title.toLowerCase()}`} icon={Icon}
        action={can(cfg.editPerm) && <button onClick={() => setOpen(true)} className={goldBtn} data-testid={`${entity}-add`}><Plus className="h-4 w-4" /> Add</button>} />

      {rows.length === 0 ? (
        <EmptyState icon={Icon} title={`No ${cfg.title.toLowerCase()} yet`} hint={can(cfg.editPerm) ? "Add your first record to get started." : "You don't have permission to add records."} />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-white/8">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/8 bg-charcoal text-left text-xs uppercase tracking-wider text-white/40">
              {cfg.fields.map((f) => <th key={f} className="px-4 py-3 capitalize">{f}</th>)}<th className="px-4 py-3" /></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`${entity}-row`}>
                  {cfg.fields.map((f) => <td key={f} className="px-4 py-3 text-white/75">{r.data?.[f] || "—"}</td>)}
                  <td className="px-4 py-3 text-right">
                    {can(cfg.editPerm) && <button onClick={() => del(r.id)} className="text-white/30 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-charcoal p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-lg font-semibold">Add {cfg.title.slice(0, -1)}</h2><button onClick={() => setOpen(false)}><X className="h-5 w-5 text-white/40" /></button></div>
            <div className="space-y-3">
              {cfg.fields.map((f) => (
                <input key={f} value={form[f] || ""} onChange={(e) => setForm({ ...form, [f]: e.target.value })} placeholder={f[0].toUpperCase() + f.slice(1)} data-testid={`${entity}-field-${f}`}
                  className="w-full rounded-xl border border-white/10 bg-graphite px-4 py-2.5 text-sm focus:border-gold/40 focus:outline-none" />
              ))}
            </div>
            <button onClick={create} className={`${goldBtn} mt-5 w-full justify-center`} data-testid={`${entity}-save`}>Save</button>
          </div>
        </div>
      )}
    </Scroller>
  );
}
